import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import copy from "copy-to-clipboard";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { BASE_URL } from "../api";
import { getApiHeaders, formatDate } from "../utils/helpers";
import { useHandleError } from "../hooks/useHandleError";

export default function DeviceManagement({ isModal = false }) {
  const navigate = useNavigate();
  const handleError = useHandleError();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [devices, setDevices] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedId, setCopiedId] = useState("");
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Identify current device
  const currentDeviceId =
    (typeof window !== "undefined" &&
      (localStorage.getItem("device_id") ||
        localStorage.getItem("deviceid"))) ||
    "";

  // Fetch active devices
  const fetchDevices = useCallback(
    async (isManualRefresh = false) => {
      if (isManualRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const res = await axios.get(`${BASE_URL}/account/device_list`, {
          headers: getApiHeaders(),
        });

        if (res.data && (res.data.code === 200 || res.data.status === true)) {
          const deviceList = Array.isArray(res.data.devices)
            ? res.data.devices
            : Array.isArray(res.data.data)
              ? res.data.data
              : [];
          setDevices(deviceList);
          if (isManualRefresh) {
            MessageToster("success", res.data.message || "Devices updated.");
          }
        } else {
          setDevices([]);
          if (isManualRefresh) {
            MessageToster("error", res.data?.message || "Failed to fetch active devices.");
          }
        }
      } catch (error) {
        console.error("Error fetching active devices:", error);
        if (isManualRefresh) {
          handleError(error);
        } else {
          setDevices([]);
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [handleError]
  );

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleOpenLogoutModal = (device) => {
    setSelectedDevice(device);
    setShowConfirmModal(true);
  };

  const handleCloseModal = () => {
    if (actionLoading) return;
    setShowConfirmModal(false);
    setSelectedDevice(null);
  };

  const handleConfirmLogout = async () => {
    if (!selectedDevice || !selectedDevice.deviceid) return;

    setActionLoading(true);
    const targetDeviceId = selectedDevice.deviceid;
    const isCurrent = targetDeviceId === currentDeviceId;

    try {
      const res = await axios.post(
        `${BASE_URL}/account/logout_by_id`,
        { deviceid: targetDeviceId },
        { headers: getApiHeaders() }
      );

      if (res.data && (res.data.code === 200 || res.data.status === true)) {
        MessageToster(
          "success",
          res.data.message || "Device session terminated successfully."
        );

        setShowConfirmModal(false);
        setSelectedDevice(null);

        if (isCurrent) {
          Cookies.remove("userdata");
          Cookies.remove("token");
          Cookies.remove("username");
          sessionStorage.removeItem("mid");
          sessionStorage.removeItem("email");
          setTimeout(() => {
            navigate("/", { replace: true });
          }, 1000);
        } else {
          fetchDevices();
        }
      } else {
        MessageToster(
          "error",
          res.data?.message || "Failed to log out device. Please try again."
        );
      }
    } catch (error) {
      handleError(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = (id) => {
    if (!id) return;
    copy(id);
    setCopiedId(id);
    MessageToster("success", "Device ID copied to clipboard");
    setTimeout(() => setCopiedId(""), 2000);
  };

  const getDeviceIcon = (deviceType) => {
    const type = (deviceType || "").toLowerCase();
    if (type.includes("mobile") || type.includes("android") || type.includes("ios")) {
      return "bx bx-mobile-alt text-primary";
    }
    if (type.includes("tablet") || type.includes("ipad")) {
      return "bx bx-tab text-info";
    }
    return "bx bx-laptop text-success";
  };

  const activeCount = devices.filter((d) => d.isactive !== false).length;

  const Content = (
    <>
      <div className="row mb-3">
        <div className="col-12">
          <div className="page-title-box d-flex align-items-center justify-content-between flex-wrap gap-2 py-2">
            <div>
              <h2 className={isModal ? "fw-700 mb-1 font-size-18" : "fw-700 mb-1 font-size-22"}>
                Device Management
              </h2>
              <p className="text-muted mb-0 font-size-13">
                Monitor active sessions and terminate unrecognized devices logged into your account.
              </p>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 shadow-sm"
                onClick={() => fetchDevices(true)}
                disabled={refreshing || loading}
              >
                <i className={`bx bx-refresh font-size-16 ${refreshing ? "bx-spin" : ""}`} />
                {refreshing ? "Refreshing..." : "Refresh"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xl-4 col-md-6 mb-3">
          <div className="card h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #556ee6" }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted text-uppercase fw-semibold font-size-12">
                  Active Sessions
                </span>
                <h3 className="fw-bold my-1 text-primary">{activeCount}</h3>
                <small className="text-muted">
                  {devices.length === 1 ? "1 Device registered" : `${devices.length} Devices registered`}
                </small>
              </div>
              <div className="avatar-sm">
                <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-22">
                  <i className="bx bx-devices"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-md-6 mb-3">
          <div className="card h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #34c38f" }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted text-uppercase fw-semibold font-size-12">
                  Current Device ID
                </span>
                <h6 className="fw-bold my-1 text-truncate" style={{ maxWidth: "220px" }}>
                  {currentDeviceId || "Auto-detected"}
                </h6>
                <small className="text-success d-flex align-items-center gap-1">
                  <span className="badge rounded-pill bg-success-subtle text-success">
                    ● This Browser
                  </span>
                </small>
              </div>
              <div className="avatar-sm">
                <span className="avatar-title rounded-circle bg-success-subtle text-success font-size-22">
                  <i className="bx bx-check-shield"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-4 col-12 mb-3">
          <div className="card h-100 border-0 shadow-sm" style={{ borderLeft: "4px solid #f1b44c" }}>
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted text-uppercase fw-semibold font-size-12">
                  Security Notice
                </span>
                <p className="font-size-12 text-muted mb-0 mt-1">
                  If you see any unknown device or unexpected IP, log it out immediately and change your password.
                </p>
              </div>
              <div className="avatar-sm ms-2">
                <span className="avatar-title rounded-circle bg-warning-subtle text-warning font-size-22">
                  <i className="bx bx-shield-quarter"></i>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className={isModal ? "card border-0 mb-0" : "card border-0 shadow-sm"}>
            <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <h5 className="card-title mb-0 fw-semibold">Active Login Devices</h5>
                <span className="badge bg-primary-subtle text-primary rounded-pill font-size-12">
                  {devices.length}
                </span>
              </div>
            </div>

            <div className="card-body">
              {devices.length === 0 && !loading ? (
                <div className="text-center py-5">
                  <div className="avatar-md mx-auto mb-3">
                    <span className="avatar-title rounded-circle bg-light text-muted font-size-24">
                      <i className="bx bx-devices"></i>
                    </span>
                  </div>
                  <h6 className="fw-semibold text-muted">No active devices found</h6>
                  <p className="text-muted font-size-13 mb-3">
                    When you log in across web or mobile apps, your active sessions will be listed here.
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => fetchDevices(true)}
                  >
                    <i className="bx bx-refresh me-1"></i> Check Again
                  </button>
                </div>
              ) : (
                <div className="row">
                  {devices.map((device, idx) => {
                    const isCurrent =
                      Boolean(currentDeviceId) &&
                      device.deviceid === currentDeviceId;
                    const isDeviceActive =
                      device.isactive !== false &&
                      (device.status || "").toLowerCase() !== "inactive";

                    return (
                      <div
                        key={device.deviceid || idx}
                        className="col-xxl-6 col-xl-6 col-12 mb-3"
                      >
                        <div
                          className={`p-3 rounded border transition-all h-100 position-relative ${isCurrent
                            ? "border-primary bg-primary-subtle bg-opacity-10 shadow-sm"
                            : "border-light-subtle bg-white shadow-sm"
                            }`}
                          style={{
                            borderWidth: isCurrent ? "1.5px" : "1px",
                            borderStyle: "solid",
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-2 mb-3 flex-wrap">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center bg-light"
                                style={{ width: "46px", height: "46px" }}
                              >
                                <i
                                  className={`${getDeviceIcon(
                                    device.device_type
                                  )} font-size-24`}
                                />
                              </div>
                              <div>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <h6 className="mb-0 fw-bold text-dark font-size-15">
                                    {(device.device_type || "Web Browser").toUpperCase()}
                                  </h6>
                                  {isCurrent && (
                                    <span className="badge bg-success font-size-11 px-2 py-1">
                                      Current Device
                                    </span>
                                  )}
                                  <span
                                    className={`badge font-size-11 px-2 py-1 ${isDeviceActive
                                      ? "bg-success-subtle text-success border border-success-subtle"
                                      : "bg-secondary-subtle text-secondary"
                                      }`}
                                  >
                                    {isDeviceActive ? "● Active" : "Inactive"}
                                  </span>
                                </div>
                                <small className="text-muted font-size-12">
                                  IP: <strong className="text-dark">{device.ip || "Unknown"}</strong>
                                </small>
                              </div>
                            </div>

                            <div>
                              <button
                                type="button"
                                className={`btn btn-sm ${isCurrent
                                  ? "btn-outline-danger"
                                  : "btn-soft-danger"
                                  } d-flex align-items-center gap-1`}
                                onClick={() => handleOpenLogoutModal(device)}
                                title={
                                  isCurrent
                                    ? "Log out from this current browser"
                                    : "Terminate this session"
                                }
                              >
                                <i className="bx bx-log-out font-size-14" />
                                <span>{isCurrent ? "Log Out (This)" : "Terminate"}</span>
                              </button>
                            </div>
                          </div>

                          <div
                            className="p-2 rounded bg-light bg-opacity-75 font-size-12 mb-2"
                            style={{ border: "1px dashed #e2e8f0" }}
                          >
                            <div className="row g-2">
                              <div className="col-12 col-sm-6">
                                <div className="text-muted d-flex align-items-center gap-1">
                                  <i className="bx bx-fingerprint font-size-14 text-secondary"></i>
                                  <span>Device ID:</span>
                                </div>
                                <div className="d-flex align-items-center gap-1 mt-1">
                                  <code
                                    className="text-primary font-size-11 text-truncate"
                                    style={{ maxWidth: "200px" }}
                                    title={device.deviceid}
                                  >
                                    {device.deviceid}
                                  </code>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-link p-0 text-muted"
                                    onClick={() => handleCopy(device.deviceid)}
                                    title="Copy Device ID"
                                  >
                                    <i
                                      className={`bx ${copiedId === device.deviceid
                                        ? "bx-check text-success font-size-14"
                                        : "bx-copy font-size-14"
                                        }`}
                                    />
                                  </button>
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="text-muted d-flex align-items-center gap-1">
                                  <i className="bx bx-globe font-size-14 text-secondary"></i>
                                  <span>IP Address:</span>
                                </div>
                                <div className="fw-semibold text-dark mt-1 font-size-12">
                                  {device.ip || "Not available"}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="text-muted d-flex align-items-center gap-1">
                                  <i className="bx bx-calendar font-size-14 text-secondary"></i>
                                  <span>Logged In At:</span>
                                </div>
                                <div className="fw-medium text-dark mt-1 font-size-11">
                                  {formatDate(device.ondate)}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="text-muted d-flex align-items-center gap-1">
                                  <i className="bx bx-time font-size-14 text-secondary"></i>
                                  <span>Last Active:</span>
                                </div>
                                <div className="fw-medium text-dark mt-1 font-size-11">
                                  {formatDate(device.update_date || device.ondate)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {isCurrent && (
                            <div className="font-size-11 text-success d-flex align-items-center gap-1 mt-2">
                              <i className="bx bx-info-circle font-size-13"></i>
                              <span>This is your active browser session right now.</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && typeof document !== "undefined" && createPortal(
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1150 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar-xs">
                    <span className="avatar-title rounded-circle bg-danger-subtle text-danger font-size-16">
                      <i className="bx bx-log-out"></i>
                    </span>
                  </div>
                  <h5 className="modal-title fw-bold text-dark mb-0">
                    {selectedDevice?.deviceid === currentDeviceId
                      ? "Log Out Current Session"
                      : "Terminate Device Session"}
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  disabled={actionLoading}
                  aria-label="Close"
                />
              </div>

              <div className="modal-body py-3">
                <p className="text-muted mb-3 font-size-14">
                  {selectedDevice?.deviceid === currentDeviceId ? (
                    <span className="text-danger fw-semibold">
                      Warning: You are about to log out from this current device. You will be signed out and redirected to the login page immediately.
                    </span>
                  ) : (
                    "Are you sure you want to terminate this device session? The device will be signed out and will need to log in again with credentials."
                  )}
                </p>

                {selectedDevice && (
                  <div className="p-3 bg-light rounded font-size-13">
                    <div className="d-flex justify-content-between py-1 border-bottom border-light-subtle">
                      <span className="text-muted">Device Type:</span>
                      <strong className="text-dark">
                        {(selectedDevice.device_type || "Web Browser").toUpperCase()}
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between py-1 border-bottom border-light-subtle">
                      <span className="text-muted">IP Address:</span>
                      <span className="text-dark fw-semibold">{selectedDevice.ip || "Unknown"}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1 border-bottom border-light-subtle">
                      <span className="text-muted">Device ID:</span>
                      <code className="text-primary font-size-11">{selectedDevice.deviceid}</code>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span className="text-muted">Logged In:</span>
                      <span className="text-dark font-size-12">{formatDate(selectedDevice.ondate)}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer border-top bg-light bg-opacity-25">
                <button
                  type="button"
                  className="btn btn-secondary waves-effect"
                  onClick={handleCloseModal}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger waves-effect waves-light d-flex align-items-center gap-1"
                  onClick={handleConfirmLogout}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" role="status" />
                      <span>Terminating...</span>
                    </>
                  ) : (
                    <>
                      <i className="bx bx-log-out font-size-14" />
                      <span>
                        {selectedDevice?.deviceid === currentDeviceId
                          ? "Confirm & Log Out"
                          : "Terminate Session"}
                      </span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.getElementById("root") || document.body
      )}
    </>
  );

  if (isModal) {
    return (
      <div className="text-start">
        <p className="text-muted mb-4 small border-bottom pb-3">
          Devices that have accessed your account. You can revoke access for any device. This device cannot be removed.
        </p>

        {/* Current Device Section */}
        <h6 className="small fw-bold mb-3 text-uppercase text-primary" style={{ letterSpacing: '1px' }}>Current Device</h6>
        {devices.filter(d => d.deviceid === currentDeviceId).map(device => (
          <div key={device.deviceid} className="p-3 mb-4 rounded-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between border border-primary gap-3">
            <div className="d-flex align-items-start align-items-sm-center gap-3 w-100">
              <div className="d-flex align-items-center justify-content-center rounded bg-primary-subtle flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                <i className="bx bx-desktop fs-3 text-primary"></i>
              </div>
              <div className="text-break flex-grow-1">
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h6 className="mb-0 fw-bold text-dark">This device</h6>
                  <span className="badge bg-primary-subtle text-primary fw-semibold" style={{ fontSize: '10px', padding: '4px 8px' }}>CURRENT DEVICE</span>
                </div>
                <p className="text-muted small mb-1">{device.device_type || "web"}</p>
                <p className="text-muted small mb-1">Device ID: <span className="text-secondary fw-medium">{device.deviceid}</span></p>
                <p className="small mb-0">
                  <span className="fw-medium text-success"><i className="bx bxs-circle" style={{ fontSize: '10px' }}></i> Active now</span>
                  <span className="text-muted ms-2">IP: {device.ip}</span>
                </p>
              </div>
            </div>
          </div>
        ))}

        {/* Other Devices Section */}
        {devices.filter(d => d.deviceid !== currentDeviceId).length > 0 && (
          <>
            <h6 className="text-muted small fw-bold mb-3 text-uppercase mt-4" style={{ letterSpacing: '1px' }}>Other Devices</h6>
            {devices.filter(d => d.deviceid !== currentDeviceId).map(device => (
              <div key={device.deviceid} className="p-3 mb-3 rounded-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between border gap-2">
                <div className="d-flex align-items-start align-items-sm-center gap-3 w-100">
                  <div className="d-flex align-items-center justify-content-center rounded bg-light flex-shrink-0" style={{ width: '48px', height: '48px' }}>
                    <i className="bx bx-desktop fs-3 text-muted"></i>
                  </div>
                  <div className="text-break flex-grow-1">
                    <h6 className="mb-1 fw-bold text-dark">Device</h6>
                    <p className="text-muted small mb-1">{device.device_type || "web"}</p>
                    <p className="text-muted small mb-1">Device ID: <span className="text-secondary fw-medium">{device.deviceid}</span></p>
                    <p className="text-muted small mb-0">Last active: {formatDate(device.update_date || device.ondate)} — IP: {device.ip}</p>
                  </div>
                </div>
                <div className="mt-2 mt-sm-0 ms-sm-auto align-self-end align-self-sm-center">
                  <button
                    className="btn btn-sm rounded px-3"
                    style={{ border: '1px solid #dc3545', color: '#dc3545', backgroundColor: 'transparent' }}
                    onClick={() => handleOpenLogoutModal(device)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {showConfirmModal && typeof document !== "undefined" && createPortal(
          <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1150 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-bottom">
                  <div className="d-flex align-items-center gap-2">
                    <div className="avatar-xs">
                      <span className="avatar-title rounded-circle bg-danger-subtle text-danger font-size-16">
                        <i className="bx bx-log-out"></i>
                      </span>
                    </div>
                    <h5 className="modal-title fw-bold text-dark mb-0">
                      {selectedDevice?.deviceid === currentDeviceId
                        ? "Log Out Current Session"
                        : "Terminate Device Session"}
                    </h5>
                  </div>
                  <button type="button" className="btn-close" onClick={handleCloseModal} disabled={actionLoading} aria-label="Close" />
                </div>
                <div className="modal-body py-3">
                  <p className="text-muted mb-3 font-size-14">
                    {selectedDevice?.deviceid === currentDeviceId ? (
                      <span className="text-danger fw-semibold">
                        Warning: You are about to log out from this current device. You will be signed out and redirected to the login page immediately.
                      </span>
                    ) : (
                      "Are you sure you want to terminate this device session? The device will be signed out and will need to log in again with credentials."
                    )}
                  </p>

                  {selectedDevice && (
                    <div className="p-3 bg-light rounded font-size-13">
                      <div className="d-flex justify-content-between py-1 border-bottom border-light-subtle">
                        <span className="text-muted">Device Type:</span>
                        <strong className="text-dark">
                          {(selectedDevice.device_type || "Web Browser").toUpperCase()}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between py-1 border-bottom border-light-subtle">
                        <span className="text-muted">IP Address:</span>
                        <span className="text-dark fw-semibold">{selectedDevice.ip || "Unknown"}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1 border-bottom border-light-subtle">
                        <span className="text-muted">Device ID:</span>
                        <code className="text-primary font-size-11">{selectedDevice.deviceid}</code>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="text-muted">Logged In:</span>
                        <span className="text-dark font-size-12">{formatDate(selectedDevice.ondate)}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer border-top bg-light bg-opacity-25">
                  <button type="button" className="btn btn-secondary waves-effect" onClick={handleCloseModal} disabled={actionLoading}>Cancel</button>
                  <button type="button" className="btn btn-danger waves-effect waves-light d-flex align-items-center gap-1" onClick={handleConfirmLogout} disabled={actionLoading}>
                    {actionLoading ? (
                      <><span className="spinner-border spinner-border-sm me-1" role="status" /><span>Terminating...</span></>
                    ) : (
                      <><i className="bx bx-log-out font-size-14" /><span>{selectedDevice?.deviceid === currentDeviceId ? "Confirm & Log Out" : "Terminate Session"}</span></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.getElementById("root") || document.body
        )}
      </div>
    );
  }

  return (
    <>
      <div id="layout-wrapper">
        {loading && <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {Content}
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
